import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { map, catchError, tap, switchMap } from 'rxjs/operators';
import { BuilderScreenBetaService } from './builder-screen.service';
import { TemplateService } from './template.service';
import { ScreenTemplate } from '../interfaces/template.models';
import { BuilderJsonOutput, BuilderNode } from '../interfaces/builder-screen.models';
import { NzMessageService } from 'ng-zorro-antd/message';
import { TranslateService } from '@ngx-translate/core';

export interface JsonTemplateConversionResult {
  success: boolean;
  template?: ScreenTemplate;
  error?: string;
  warnings?: string[];
}

export interface JsonLoadResult {
  success: boolean;
  nodesCount?: number;
  error?: string;
  warnings?: string[];
}

@Injectable({
  providedIn: 'root'
})
export class JsonTemplateIntegrationService {

  constructor(
    private builderService: BuilderScreenBetaService,
    private templateService: TemplateService,
    private message: NzMessageService,
    private translate: TranslateService
  ) {}

  /**
   * Converte JSON do builder para template
   */
  convertBuilderJsonToTemplate(
    jsonData: BuilderJsonOutput | any, 
    templateName: string, 
    templateDescription: string,
    category: string = 'custom'
  ): Observable<JsonTemplateConversionResult> {
    try {
      const template: ScreenTemplate = {
        id: `template-${Date.now()}`,
        name: templateName,
        description: templateDescription,
        category: category as any,
        components: this.convertJsonToTemplateComponents(jsonData),
        gridConfig: {
          columns: 24,
          rows: 20,
          gap: '8px'
        },
        createdAt: new Date(),
        updatedAt: new Date()
      };

      return this.templateService.addTemplate(template).pipe(
        map(success => ({
          success,
          template: success ? template : undefined,
          error: success ? undefined : 'Falha ao salvar template'
        })),
        catchError(error => of({
          success: false,
          error: error.message || 'Erro desconhecido ao criar template'
        }))
      );
    } catch (error: any) {
      return of({
        success: false,
        error: error.message || 'Erro ao processar JSON para template'
      });
    }
  }

  /**
   * Carrega template como JSON no builder
   */
  loadTemplateAsJson(templateId: string): Observable<JsonLoadResult> {
    return this.templateService.getTemplateById(templateId).pipe(
      map(template => {
        if (!template) {
          return {
            success: false,
            error: 'Template não encontrado'
          };
        }

        const builderNodes = this.convertTemplateToBuilderNodes(template);
        this.builderService.loadFromJson(builderNodes);

        return {
          success: true,
          nodesCount: builderNodes.length,
          warnings: this.validateTemplateConversion(template, builderNodes)
        };
      }),
      catchError(error => of({
        success: false,
        error: error.message || 'Erro ao carregar template'
      }))
    );
  }

  /**
   * Aplica JSON diretamente ao builder com validação
   */
  applyJsonToBuilder(jsonData: any, showMessages = true): Observable<JsonLoadResult> {
    try {
      // Validação prévia
      const validation = this.validateJsonStructure(jsonData);
      if (!validation.isValid) {
        if (showMessages) {
          this.translate.get('builderScreen.jsonEditor.invalidStructure')
            .subscribe(msg => this.message.error(msg));
        }
        return of({
          success: false,
          error: validation.error,
          warnings: validation.warnings
        });
      }

      // Aplica ao builder
      this.builderService.loadFromJson(jsonData);

      if (showMessages) {
        this.translate.get('builderScreen.jsonEditor.appliedSuccessfully')
          .subscribe(msg => this.message.success(msg));
      }

      return of({
        success: true,
        nodesCount: Array.isArray(jsonData) ? jsonData.length : 1,
        warnings: validation.warnings
      });

    } catch (error: any) {
      if (showMessages) {
        this.translate.get('builderScreen.jsonEditor.applyError')
          .subscribe(msg => this.message.error(msg));
      }

      return of({
        success: false,
        error: error.message || 'Erro ao aplicar JSON'
      });
    }
  }

  /**
   * Exporta estado atual do builder como template
   */
  exportCurrentStateAsTemplate(
    templateName: string, 
    templateDescription: string,
    category: string = 'user-created'
  ): Observable<JsonTemplateConversionResult> {
    return this.builderService.jsonOutput$.pipe(
      switchMap(jsonOutput => this.convertBuilderJsonToTemplate(
        jsonOutput, 
        templateName, 
        templateDescription, 
        category
      )),
      catchError(error => of({
        success: false,
        error: error.message || 'Erro ao exportar estado atual'
      }))
    );
  }

  /**
   * Valida estrutura do JSON antes de aplicar
   */
  private validateJsonStructure(jsonData: any): {
    isValid: boolean;
    error?: string;
    warnings?: string[];
  } {
    const warnings: string[] = [];

    if (!jsonData) {
      return {
        isValid: false,
        error: 'JSON está vazio ou nulo'
      };
    }

    // Validação para estrutura de output completa
    if (jsonData.tabs && Array.isArray(jsonData.tabs)) {
      if (jsonData.tabs.length === 0) {
        warnings.push('Nenhuma aba encontrada no JSON');
      }

      if (jsonData.panels && !Array.isArray(jsonData.panels)) {
        return {
          isValid: false,
          error: 'Propriedade "panels" deve ser um array'
        };
      }

      if (jsonData.fields && !Array.isArray(jsonData.fields)) {
        return {
          isValid: false,
          error: 'Propriedade "fields" deve ser um array'
        };
      }

      return { isValid: true, warnings };
    }

    // Validação para array de nodes
    if (Array.isArray(jsonData)) {
      if (jsonData.length === 0) {
        warnings.push('Array de componentes está vazio');
      }

      const invalidNodes = jsonData.filter(node => !node.id || !node.type);
      if (invalidNodes.length > 0) {
        warnings.push(`${invalidNodes.length} componente(s) sem ID ou tipo válido`);
      }

      return { isValid: true, warnings };
    }

    // Validação para node individual
    if (jsonData.id && jsonData.type) {
      return { isValid: true, warnings };
    }

    return {
      isValid: false,
      error: 'Estrutura JSON não reconhecida. Esperado: array de componentes, objeto com tabs/panels/fields, ou componente individual'
    };
  }

  /**
   * Converte JSON output para componentes de template
   */
  private convertJsonToTemplateComponents(jsonData: BuilderJsonOutput): any[] {
    const components: any[] = [];

    // Processa tabs
    if (jsonData.tabs) {
      jsonData.tabs.forEach((tab, tabIndex) => {
        components.push({
          id: `tab-${tabIndex}`,
          type: 'tabs',
          props: {
            label: tab.name,
            icon: tab.icon,
            themeIcon: tab.themeIcon,
            twotoneColor: tab.twotoneColor,
            disabled: tab.disabled
          },
          position: { row: tabIndex, col: 0 }
        });
      });
    }

    // Processa panels
    if (jsonData.panels) {
      jsonData.panels.forEach((panel, panelIndex) => {
        components.push({
          id: `panel-${panelIndex}`,
          type: 'section',
          props: {
            label: panel.name,
            active: panel.active,
            disabled: panel.disabled
          },
          position: { row: panelIndex, col: 0 }
        });
      });
    }

    // Processa fields
    if (jsonData.fields) {
      jsonData.fields.forEach((field, fieldIndex) => {
        components.push({
          id: `field-${fieldIndex}`,
          type: field.type,
          props: {
            label: field.label,
            placeholder: field.placeholder,
            field: field.field,
            colSpan: field.colSpan,
            ...(field.type === 'textarea' && { minRows: (field as any).minRows })
          },
          position: { row: field.row || fieldIndex, col: field.col || 0 }
        });
      });
    }

    return components;
  }

  /**
   * Converte template para BuilderNodes
   */
  private convertTemplateToBuilderNodes(template: ScreenTemplate): BuilderNode[] {
    return template.components.map((component, index) => ({
      id: `component-${index}`,
      type: component.type as any,
      props: component.props,
      position: component.position,
      children: []
    }));
  }

  /**
   * Valida conversão de template
   */
  private validateTemplateConversion(template: ScreenTemplate, nodes: BuilderNode[]): string[] {
    const warnings: string[] = [];

    if (template.components.length !== nodes.length) {
      warnings.push(`Número de componentes difere: template tem ${template.components.length}, builder tem ${nodes.length}`);
    }

    const unsupportedTypes = template.components
      .filter((comp, index) => !nodes.find(node => node.id === `component-${index}`))
      .map(comp => comp.type);

    if (unsupportedTypes.length > 0) {
      warnings.push(`Tipos não suportados: ${unsupportedTypes.join(', ')}`);
    }

    return warnings;
  }

  /**
   * Obtém estatísticas do JSON
   */
  getJsonStatistics(jsonData: any): {
    totalComponents: number;
    componentTypes: Record<string, number>;
    hasValidation: boolean;
    estimatedComplexity: 'low' | 'medium' | 'high';
  } {
    let totalComponents = 0;
    const componentTypes: Record<string, number> = {};

    if (Array.isArray(jsonData)) {
      totalComponents = jsonData.length;
      jsonData.forEach(item => {
        const type = item.type || 'unknown';
        componentTypes[type] = (componentTypes[type] || 0) + 1;
      });
    } else if (jsonData.tabs || jsonData.panels || jsonData.fields) {
      totalComponents = (jsonData.tabs?.length || 0) + 
                      (jsonData.panels?.length || 0) + 
                      (jsonData.fields?.length || 0);

      ['tabs', 'panels', 'fields'].forEach(key => {
        if (jsonData[key]) {
          jsonData[key].forEach((item: any) => {
            const type = item.type || key.slice(0, -1); // Remove 's' from key
            componentTypes[type] = (componentTypes[type] || 0) + 1;
          });
        }
      });
    }

    const complexity = totalComponents <= 5 ? 'low' : 
                      totalComponents <= 15 ? 'medium' : 'high';

    return {
      totalComponents,
      componentTypes,
      hasValidation: Object.keys(componentTypes).some(type => 
        ['text', 'number', 'select', 'date'].includes(type)
      ),
      estimatedComplexity: complexity
    };
  }
}
